// Branded client-side PDF export for the norse3 Governance Tools.
// Educational only — every report carries the relevant disclaimer.
import {
  GOV_DISCLAIMER_ASSESS, GOV_DISCLAIMER_QA, GOV_DISCLAIMER_DEPLOY, GOV_DISCLAIMER_ROI,
  GOV_DISCLAIMER_FRAMEWORK, ANSWERS, SCORED_QUESTIONS, EXPOSURE_QUESTIONS,
  DEPLOY_QUESTIONS, POLICY_TEST_SAMPLES, EVIDENCE_ITEMS, FRAMEWORKS, SKJOLD_LABELS,
} from "./govData";

const PAGE = { w: 595.28, h: 841.89 };
const M = 42;
const CW = PAGE.w - M * 2;

const NAVY = [5, 7, 15];
const CYAN_BAR = [56, 217, 240];
const CYAN_TEXT = [13, 110, 138];
const AMBER_TEXT = [176, 120, 10];
const GREEN_TEXT = [22, 130, 86];
const INK = [28, 32, 40];
const MUTED = [110, 120, 135];
const LINE = [224, 228, 234];

// ---- shared compute helpers (mirror the tool components) ----
export const answerLabel = (v) => ANSWERS.find((a) => a.value === v)?.label ?? "\u2014";

export function policySentence(cfg) {
  return `IF ${cfg.trigger.toLowerCase()} is detected in a ${cfg.userType.toLowerCase()} ${cfg.channel.toLowerCase()} interaction, THEN ${cfg.action.toLowerCase()}${cfg.evidence.length ? " AND " + cfg.evidence.map((e) => e.toLowerCase()).join(" AND ") : ""}.`;
}

export const CURRENCY_SYMBOLS = { GBP: "\u00a3", USD: "$", EUR: "\u20ac" };
export const CURRENCIES = [
  { code: "GBP", label: "GBP (\u00a3)", symbol: "\u00a3" },
  { code: "USD", label: "USD ($)", symbol: "$" },
  { code: "EUR", label: "EUR (\u20ac)", symbol: "\u20ac" },
];
const clampPct = (n) => Math.min(100, Math.max(0, Number(n) || 0));

export function computeRoi(v) {
  const volume = Math.max(0, Number(v.volume) || 0);
  const currentSampling = clampPct(v.currentSampling);
  const targetCoverage = clampPct(v.targetCoverage);
  const minutes = Math.max(0, Number(v.minutes) || 0);
  const cost = Math.max(0, Number(v.cost) || 0);
  const currentReviewed = Math.round(volume * (currentSampling / 100));
  const targetScreened = Math.round(volume * (targetCoverage / 100));
  const extraScreened = Math.max(0, targetScreened - currentReviewed);
  const coverageGapPts = Math.max(0, targetCoverage - currentSampling);
  const monthlyHours = (targetScreened * minutes) / 60;
  const annualHours = monthlyHours * 12;
  const monthlyCost = monthlyHours * cost;
  const annualCost = annualHours * cost;
  const fte = monthlyHours / 150;
  return { currentReviewed, targetScreened, extraScreened, coverageGapPts, monthlyHours, annualHours, monthlyCost, annualCost, fte, currentSampling, targetCoverage };
}

export function computeQa(v) {
  const reviewed = Math.round(v.interactions * (v.pctReviewed / 100));
  const notReviewed = v.interactions - reviewed;
  const manualHours = (reviewed * v.minutes) / 60;
  const manualCost = manualHours * v.cost;
  const riskVolume = Math.round(v.interactions * (v.targetCoverage / 100) * (v.pctHuman / 100));
  const riskHours = (riskVolume * v.minutes) / 60;
  return { reviewed, notReviewed, manualHours, manualCost, coverage: v.pctReviewed, riskVolume, riskHours, hoursDiff: manualHours - riskHours };
}

export function deployCategory(d) {
  if (d.infra === "Customer-controlled environment required" || d.sensitivity === "Highly sensitive regulated information") return "Customer-Controlled";
  if (d.infra === "Private cloud preferred" || d.sensitivity === "Financial information" || d.residency === "Specific jurisdiction") return "Private Cloud";
  return "Hosted";
}

const DEPLOY_FLOW = ["Customer", "Chat interface", "Bifr\u00f6st Relay", "Aegis Engine", "Organisation policy", "Allow / Warn / Redact / Block / Escalate", "Underlying model", "Heimdall oversight & evidence"];

// ---- low-level render primitives ----
function ensure(ctx, need) {
  if (ctx.y + need > PAGE.h - 58) { ctx.doc.addPage(); ctx.y = 56; }
}

function drawBand(doc, title) {
  doc.setFillColor(...NAVY); doc.rect(0, 0, PAGE.w, 74, "F");
  doc.setFillColor(...CYAN_BAR); doc.rect(0, 74, PAGE.w, 3, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(255, 255, 255);
  doc.text("norse3", M, 36);
  doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(150, 220, 238);
  doc.text(title, M, 56);
  doc.setFontSize(8); doc.setTextColor(150, 160, 175);
  doc.text("Generated " + new Date().toLocaleString("en-GB"), PAGE.w - M, 56, { align: "right" });
}

function footerAll(doc, disclaimer) {
  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i);
    doc.setDrawColor(...LINE); doc.setLineWidth(0.5);
    doc.line(M, PAGE.h - 44, PAGE.w - M, PAGE.h - 44);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...MUTED);
    const lines = doc.splitTextToSize(disclaimer, CW - 70);
    doc.text(lines, M, PAGE.h - 34);
    doc.text(`Page ${i} of ${n}`, PAGE.w - M, PAGE.h - 20, { align: "right" });
    doc.text("norse3 governance tools \u00b7 educational use only", M, PAGE.h - 20);
  }
}

function heading(ctx, text) {
  ensure(ctx, 30);
  const { doc } = ctx;
  doc.setFillColor(...CYAN_BAR); doc.rect(M, ctx.y - 9, 3, 13, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...INK);
  doc.text(text, M + 10, ctx.y);
  ctx.y += 20;
}

function kvRows(ctx, rows) {
  const { doc } = ctx;
  rows.forEach(([k, v]) => {
    const val = doc.splitTextToSize(String(v ?? "\u2014"), CW - 165);
    const need = Math.max(15, val.length * 13);
    ensure(ctx, need);
    doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(...MUTED);
    doc.text(doc.splitTextToSize(String(k), 150), M + 10, ctx.y);
    doc.setFont("helvetica", "normal"); doc.setTextColor(...INK);
    doc.text(val, M + 168, ctx.y);
    ctx.y += need + 4;
  });
}

function bullets(ctx, items, color) {
  const { doc } = ctx;
  items.forEach((t) => {
    const lines = doc.splitTextToSize(String(t), CW - 26);
    const need = Math.max(14, lines.length * 13);
    ensure(ctx, need);
    doc.setFillColor(...(color || CYAN_TEXT)); doc.circle(M + 12, ctx.y - 3, 1.6, "F");
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(...INK);
    doc.text(lines, M + 20, ctx.y);
    ctx.y += need + 3;
  });
}

function note(ctx, text) {
  const { doc } = ctx;
  const lines = doc.splitTextToSize(String(text), CW - 16);
  const need = lines.length * 12;
  ensure(ctx, need + 4);
  doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(...MUTED);
  doc.text(lines, M + 10, ctx.y);
  doc.setFont("helvetica", "normal");
  ctx.y += need + 6;
}

function groupTitle(ctx, text) {
  ensure(ctx, 34);
  const { doc } = ctx;
  doc.setFillColor(244, 247, 250); doc.rect(M - 6, ctx.y - 13, CW + 12, 22, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(...CYAN_TEXT);
  doc.text(text, M + 4, ctx.y + 2);
  ctx.y += 26;
}

function renderSection(ctx, s) {
  if (s.heading) heading(ctx, s.heading);
  if (s.kv?.length) kvRows(ctx, s.kv);
  if (s.bullets?.length) { ctx.y += 2; bullets(ctx, s.bullets, s.bulletColor); }
  if (s.note) { ctx.y += 2; note(ctx, s.note); }
  ctx.y += 8;
}

async function save({ docTitle, fileName, groups, disclaimer }) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  drawBand(doc, docTitle);
  const ctx = { doc, y: 104 };
  groups.forEach((g, gi) => {
    if (g.title) { if (gi > 0) ctx.y += 6; groupTitle(ctx, g.title); }
    g.sections.forEach((s) => renderSection(ctx, s));
  });
  footerAll(doc, disclaimer);
  doc.save(fileName.endsWith(".pdf") ? fileName : fileName + ".pdf");
}

// ---- per-tool report groups ----
export function assessmentGroup(result, answers, exposure) {
  const dims = result.dims || {};
  const gaps = result.gaps || [];
  const strongest = result.strongest || [];
  const support = result.support || {};
  const dimRows = Object.entries(dims).map(([d, val]) => [d, `${val} / 100`]);
  const nextActions = gaps.flatMap((d) => {
    const s = support[d];
    return s ? [`${d} \u2014 ${s.action}`, `norse3: ${s.norse3}`] : [`${d} \u2014 review this area with your governance team`];
  });
  return {
    title: "Readiness Assessment",
    sections: [
      { heading: "Overall result", kv: [["Maturity band", result.band?.band || "\u2014"], ["Governance index", `${result.overall ?? 0} / 100`], ["Summary", result.band?.blurb || "\u2014"], ["Completion", result.complete ? "All questions answered" : "Partial \u2014 answer the rest for a fuller profile"]] },
      { heading: "Dimension scores", kv: dimRows },
      { heading: "Strongest areas", bullets: strongest.length ? strongest : ["\u2014"], bulletColor: GREEN_TEXT },
      { heading: "Priority gaps", bullets: gaps.length ? gaps : ["\u2014"], bulletColor: AMBER_TEXT },
      ...(nextActions.length ? [{ heading: "Suggested next actions", bullets: nextActions }] : []),
      { heading: "Your context", kv: EXPOSURE_QUESTIONS.map((q) => [q.text, answerLabel(exposure?.[q.id])]) },
      { heading: "Governance control responses", kv: SCORED_QUESTIONS.map((q) => [q.text, answerLabel(answers?.[q.id])]) },
    ],
  };
}

export function policyGroup(cfg) {
  return {
    title: "Policy Control Builder",
    sections: [
      { heading: "Policy rule", kv: [["Trigger", cfg.trigger], ["User type", cfg.userType], ["Channel", cfg.channel], ["Severity", cfg.severity], ["Action", cfg.action]] },
      { heading: "Evidence to capture", bullets: cfg.evidence.length ? cfg.evidence : ["None selected"], bulletColor: GREEN_TEXT },
      { heading: "Plain-English rule", note: policySentence(cfg) },
      { heading: "Synthetic test", kv: [["Sample interaction", POLICY_TEST_SAMPLES[cfg.trigger] || "\u2014"], ["Detected", cfg.trigger], ["Applied action", cfg.action], ["Evidence records", String(cfg.evidence.length)]], note: "norse3 applies your rule exactly as configured \u2014 it does not invent its own regulatory decision." },
    ],
  };
}

export function roiGroup(v) {
  const c = computeRoi(v);
  const sym = CURRENCY_SYMBOLS[v.currency] || CURRENCY_SYMBOLS.GBP;
  const num = (n) => Math.round(n).toLocaleString("en-GB");
  const money = (n) => sym + num(n);
  return {
    title: "AI Governance ROI & Value Calculator",
    sections: [
      { heading: "Your inputs", kv: [
        ["Monthly conversation volume", c.targetScreened >= 0 ? Number(v.volume || 0).toLocaleString("en-GB") : "\u2014"],
        ["Currently screened manually", v.currentSampling + "%"],
        ["Target screening coverage", v.targetCoverage + "%"],
        ["Manual review time per conversation", v.minutes + " min"],
        ["Reviewer cost", money(v.cost) + "/hr"],
        ["Currency", (v.currency || "GBP")],
      ] },
      { heading: "Coverage impact", kv: [
        ["Conversations screened today", c.currentReviewed.toLocaleString("en-GB") + "/mo"],
        ["Conversations screened at target", c.targetScreened.toLocaleString("en-GB") + "/mo"],
        ["Additional conversations screened", "+" + c.extraScreened.toLocaleString("en-GB") + "/mo"],
        ["Coverage gap closed", c.coverageGapPts.toFixed(0) + " percentage points"],
      ], note: `Screening coverage moves from ${c.currentSampling}% to ${c.targetCoverage}% of conversations, with reviewers focused on flagged interactions.` },
      { heading: "Manual-equivalent effort automated", kv: [
        ["Hours saved per month", num(c.monthlyHours) + " hrs"],
        ["Hours saved per year", num(c.annualHours) + " hrs"],
        ["Full-time reviewer equivalent", c.fte.toFixed(1) + " FTE"],
      ] },
      { heading: "Illustrative cost saved", kv: [
        ["Per month", money(c.monthlyCost)],
        ["Per year", money(c.annualCost)],
      ], note: "Illustrative business case only \u2014 not a guarantee of savings." },
    ],
  };
}

export function qaGroup(v) {
  const c = computeQa(v);
  const num = (n) => Math.round(n).toLocaleString("en-GB");
  return {
    title: "QA Coverage & Resource Calculator",
    sections: [
      { heading: "Your current process", kv: [["Monthly interactions", v.interactions.toLocaleString("en-GB")], ["QA reviewers", String(v.reviewers)], ["Currently reviewed", v.pctReviewed + "%"], ["Minutes per review", v.minutes + " min"], ["Reviewer cost", "\u00a3" + v.cost + "/hr"]] },
      { heading: "Current model \u00b7 sample-based manual QA", kv: [["Interactions reviewed", c.reviewed.toLocaleString("en-GB")], ["Not manually reviewed", c.notReviewed.toLocaleString("en-GB")], ["Manual review hours", num(c.manualHours)], ["Estimated manual QA cost", "\u00a3" + num(c.manualCost)]] },
      { heading: "Illustrative risk-based model", kv: [["Automated screening coverage", v.targetCoverage + "%"], ["Expected human review", v.pctHuman + "% flagged"], ["Risk-based human review volume", c.riskVolume.toLocaleString("en-GB")], ["Review hours (risk-based)", num(c.riskHours)], ["Indicative reviewer-hour difference", num(c.hoursDiff) + " hrs/month"]], note: `Screening coverage could expand from ${c.coverage}% toward ${v.targetCoverage}% with targeted human review.` },
    ],
  };
}

export function deployGroup(d) {
  return {
    title: "Deployment Configurator",
    sections: [
      { heading: "Indicative deployment", kv: [["Recommended pattern", deployCategory(d)], ["Questions answered", `${DEPLOY_QUESTIONS.filter((q) => d[q.id]).length} of ${DEPLOY_QUESTIONS.length}`]] },
      { heading: "Your selections", kv: DEPLOY_QUESTIONS.map((q) => [q.text, d[q.id] || "\u2014"]) },
      { heading: "Indicative data flow", bullets: DEPLOY_FLOW },
    ],
  };
}

export function frameworkGroup(active, rows) {
  return {
    title: `Framework mapping \u2014 ${active}`,
    sections: [
      ...rows.map((r) => ({ heading: r.theme, kv: [["Operational question", r.question], ["norse3 control", r.control]] })),
      { heading: "Evidence created", bullets: EVIDENCE_ITEMS, bulletColor: GREEN_TEXT },
    ],
  };
}

export function documentControlsGroup(skjold) {
  const label = SKJOLD_LABELS[skjold] || "Not specified";
  const note = skjold === "in_place"
    ? "Skjold scans documents locally before they are shared with AI tools and blocks restricted material such as passports, bank statements, IDs and proof of address."
    : skjold === "planned"
    ? "A document guard (Skjold) is planned but not yet operational. Staff may still be able to share restricted documents with AI tools."
    : "No document guard is in place. Staff may be able to share restricted documents with AI tools.";
  return {
    title: "Document controls",
    sections: [
      { heading: "Skjold document guard", kv: [["Status", label]], note },
    ],
  };
}

// ---- public single-tool exports ----
export const savePdf = {
  assessment: (result, answers, exposure) => save({ docTitle: "Governance readiness assessment", fileName: "norse3-readiness-assessment", groups: [assessmentGroup(result, answers, exposure)], disclaimer: GOV_DISCLAIMER_ASSESS }),
  policy: (cfg) => save({ docTitle: "Policy control summary", fileName: "norse3-policy-summary", groups: [policyGroup(cfg)], disclaimer: "Example policy for educational purposes only and does not constitute legal, regulatory or compliance advice." }),
  qa: (v) => save({ docTitle: "QA coverage & resource model", fileName: "norse3-qa-model", groups: [qaGroup(v)], disclaimer: GOV_DISCLAIMER_QA }),
  roi: (v) => save({ docTitle: "AI governance ROI & value business case", fileName: "norse3-roi-business-case", groups: [roiGroup(v)], disclaimer: GOV_DISCLAIMER_ROI }),
  deploy: (d) => save({ docTitle: "Indicative deployment configuration", fileName: "norse3-deployment-configuration", groups: [deployGroup(d)], disclaimer: GOV_DISCLAIMER_DEPLOY }),
  framework: (active, rows) => save({ docTitle: "Regulatory framework mapping", fileName: `norse3-framework-${active.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`, groups: [frameworkGroup(active, rows)], disclaimer: GOV_DISCLAIMER_FRAMEWORK }),
  snapshot: (state) => { const { groups, disclaimer } = snapshotGroups(state); return save({ docTitle: "Governance tools \u2014 session report", fileName: "norse3-governance-report", groups, disclaimer }); },
};

// ---- combined session report ----
export function hasReportData(state) {
  return !!(state.result || state.policy || state.qa || state.roi || (state.deploy && Object.keys(state.deploy).length) || state.framework || state.skjold);
}

export function snapshotGroups(state) {
  const groups = [];
  const disc = [];
  if (state.result) { groups.push(assessmentGroup(state.result, state.answers, state.exposure)); disc.push(GOV_DISCLAIMER_ASSESS); }
  if (state.skjold) { groups.push(documentControlsGroup(state.skjold)); }
  if (state.policy) { groups.push(policyGroup(state.policy)); }
  if (state.qa) { groups.push(qaGroup(state.qa)); disc.push(GOV_DISCLAIMER_QA); }
  if (state.roi) { groups.push(roiGroup(state.roi)); disc.push(GOV_DISCLAIMER_ROI); }
  if (state.deploy && Object.keys(state.deploy).length) { groups.push(deployGroup(state.deploy)); disc.push(GOV_DISCLAIMER_DEPLOY); }
  if (state.framework && FRAMEWORKS[state.framework]) { groups.push(frameworkGroup(state.framework, FRAMEWORKS[state.framework])); disc.push(GOV_DISCLAIMER_FRAMEWORK); }
  const disclaimer = "Educational only \u2014 not legal, regulatory or compliance advice. " + Array.from(new Set(disc)).join(" ");
  return { groups, disclaimer };
}

export function saveCombined(state) {
  const { groups, disclaimer } = snapshotGroups(state);
  return save({ docTitle: "Governance tools \u2014 session report", fileName: "norse3-governance-report", groups, disclaimer });
}
